import * as React from "react";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { Form, useFormApi, Checkbox, Option, useFormState } from "informed";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import { Dropdown } from "../../../forms/informedv2/Dropdown";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { ChallengeDecisionMaker, StudyFvcSetting, UnitType } from "../../../misc/api/apiTypes";
import { useParams, Link } from "react-router-dom";
import { AuditModal, AuditForm } from "../../../auditmodal/components/AuditModal";
import { ActionButtons } from "../../../misc/ActionButtons";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { ParameterEntity } from "../../../misc/api/SerenityReferenceData";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { ConvertToSelectListItemArray } from "./ParameterSelectList";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { ISelectListItem } from "../../../misc/AspnetSelectList";

declare const rootUrl: string;

interface FvcSettingsScreenFormState {
    acceptableTests: number;
    maximumTests: number;
    testAcceptanceChange: boolean;
    testAcceptanceChangeWindow: number;
    selectedTestParameters: ISelectListItem[];
    atsGuidelineRevision: string;
    atsFVCRepeatability: boolean;
    atsFVCRepeatabilityValue: number;
    atsFEV1Repeatability: boolean;
    atsFEV1RepeatabilityValue: number;
    gradingEnabled: boolean;
    decisionMaker: ChallengeDecisionMaker;
    overreadEnabled: boolean;
    selectedAnalysisParameters: ISelectListItem[];
    selectedRepeatabilityParameters: ISelectListItem[];
    predictedValueCalculationsEnabled: boolean;
    technicianChallengeOverreaderDecision: boolean;
    fivePercentFvc: boolean;
}

interface FvcSettingsScreenFormProps {
    studyId: string;
    parameters: ParameterEntity[];
}

export const FvcSettingsScreenForm = ({ studyId, parameters }: FvcSettingsScreenFormProps) => {
    const formApi = useFormApi<FvcSettingsScreenFormState>();
    const formState = useFormState<FvcSettingsScreenFormState>();
    const [unitTypes, setUnitTypes] = React.useState<UnitType[]>([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const legacyClient = useWebPortalLegacyClient();

    const parametersSelectList: ISelectListItem[] = parameters.map(p => {
        return {
            Id: p.Id,
            Value: p.Keyword,
            Text: p.DisplayName + (p.UnitName ? ` (${p.UnitName})` : ''),
            Group: null,
            Selected: false,
            Disabled: false,
            Category: p.Category,
        };
    });

    const onPreAuditSubmit = () => {
        setAlertShown(false);
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {

        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        const fvcSettings: StudyFvcSetting = {
            SelectedTestParameters: convertToIdsArray(state.values.selectedTestParameters),
            AtsDeviationsFvc: state.values.atsFVCRepeatability,
            AtsDeviationsFvcVolume: state.values.atsFVCRepeatabilityValue,
            AtsDeviationsFev1: state.values.atsFEV1Repeatability,
            AtsDeviationsFev1Volume: state.values.atsFEV1RepeatabilityValue,
            AcceptableTests: state.values.acceptableTests,
            MaxTests: state.values.maximumTests,
            SelectedFinalTestAcceptabilityDecisionMaker: state.values.decisionMaker,
            IsOverreadingAvailable: state.values.overreadEnabled,
            TestAcceptanceChange: state.values.testAcceptanceChange,
            TestAcceptanceChangeTimeWindow: state.values.testAcceptanceChangeWindow,
            GradingEnabled: state.values.gradingEnabled,
            AtsGuidelineRevision: state.values.atsGuidelineRevision,
            SelectedAnalysisParameters: convertToIdsArray(state.values.selectedAnalysisParameters),
            SelectedRepeatabilityParameters: convertToIdsArray(state.values.selectedRepeatabilityParameters),
            PredictedValueCalculationsEnabled: state.values.predictedValueCalculationsEnabled,
            TechnicianChallengeOverreaderDecision: state.values.technicianChallengeOverreaderDecision
            
        }

        const payload = JSON.stringify(fvcSettings);

        try {
            const result = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Fvc.StudyFvcSetting", payload);

            const fivePercentResult = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Fvc.DisplayFivePercentOfFvcSetting", state.values.fivePercentFvc);

            if (result && fivePercentResult) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.Message);
        }

        setSubmitting(false);
    };

    const convertToIdsArray = (params: ISelectListItem[]) => {
        return params.length > 0
            ? parameters.filter(p => params.map(s => s.Value).includes(p.Keyword)).map(p => p.Id)
            : null;
    }

    const AcceptableToMaximumValidator = (value: number, threshold: number) => {
        return !value || value > threshold ? "'Acceptable Tests' field cannot be greater than the 'Maximum Tests' field" : undefined;
    }

    const acceptableTestValidation = (value: number, values: FvcSettingsScreenFormState) => {
        return IntegerValidator(value) || MinMaxValidator(value, 1, 8) || AcceptableToMaximumValidator(values.acceptableTests, values.maximumTests)
    }

    const maximumTestValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 4, 8)
    }

    const maximum3Decimals = (value: number) => {
        return value === Math.floor(value) ? undefined : value.toString().split(".")[1].length > 3 ? "A maximum of 3 decimal places is supported" : undefined;
    }

    const repeatabilityParameterOptions = parametersSelectList.filter(p => p.Category === 'RepeatabilityParameter');
    const analysisParameterOptions = parametersSelectList.filter(p => p.Category === 'AnalysisParameter');

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">FVC Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Acceptable Tests</label>
                                    <p className="help-block">Configures the number of acceptable tests required to comprise a valid session</p>
                                    <TextBox<number, FvcSettingsScreenFormState> id="acceptableTests" field="acceptableTests" type="number" min="1" max="8" required validate={acceptableTestValidation} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Maximum Tests</label>
                                    <p className="help-block">Configures the maximum number of tests allowed</p>
                                    <TextBox<number, FvcSettingsScreenFormState> id="maximumTests" field="maximumTests" type="number" min="4" max="8" required validate={maximumTestValidation} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Test Acceptance Change Allowed</label>
                                    <p className="help-block">Configures whether test accceptance changes are allowed by the technician and the window of time after session completion</p>
                                    <Checkbox<boolean, FvcSettingsScreenFormState> id="testAcceptanceChange" field="testAcceptanceChange" />
                                    <TextBoxWithStaticSuffix<number, FvcSettingsScreenFormState> id="testAcceptanceChangeWindow" field="testAcceptanceChangeWindow" type="number" min="0" max="120" staticpart="Hours" disabled={!formState.values.testAcceptanceChange} validate={(value) => IntegerValidator(value) || MinMaxValidator(value, 0, 120)} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Quality Grading</label>
                                    <p className="help-block">Configures whether the ATS session quality grading is displayed or not</p>
                                    <Checkbox<boolean, FvcSettingsScreenFormState> id="gradingEnabled" field="gradingEnabled" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Displayed Test Parameters</label>
                                    <p className="help-block">The test parameters to be displayed on subject testing screens in Spirotrac and Web Portal home spirometry review</p>
                                    <MultiSelect
                                        id="selectedTestParameters"
                                        field="selectedTestParameters"
                                        options={analysisParameterOptions}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>ATS/ERS Guidelines</label>
                                    <p className="help-block">The ATS/ERS guideline revision to use for test quality</p>
                                    <Dropdown field="atsGuidelineRevision">
                                        <Option id="2005">ATS/ERS 2005</Option>
                                        <Option id="2019">ATS/ERS 2019</Option>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>ATS FVC Repeatability</label>
                                    <p className="help-block">Whether deviations from ATS guidelines in calculation of FVC Repeatability are allowed</p>
                                    <Checkbox id="atsFVCRepeatability" field="atsFVCRepeatability" />
                                    <TextBoxWithStaticSuffix<number, FvcSettingsScreenFormState> id="atsFVCRepeatabilityValue" field="atsFVCRepeatabilityValue" type="number" min="0.10" max="1.00" step="0.05" staticpart="Litres" disabled={!formState.values.atsFVCRepeatability} validate={(value) => MinMaxValidator(value, 0.1, 1.0) || maximum3Decimals(value)} />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>ATS FEV1 Repeatability</label>
                                    <p className="help-block">Whether deviations from ATS guidelines in calculation of FEV1 Repeatability are allowed</p>
                                    <Checkbox id="atsFEV1Repeatability" field="atsFEV1Repeatability" />
                                    <TextBoxWithStaticSuffix<number, FvcSettingsScreenFormState> id="atsFEV1RepeatabilityValue" field="atsFEV1RepeatabilityValue" type="number" min="0.10" max="1.00" step="0.05" staticpart="Litres" disabled={!formState.values.atsFEV1Repeatability} validate={(value) => MinMaxValidator(value, 0.1, 1.0) || maximum3Decimals(value)} />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Enable Predicted Value Calculations</label>
                                    <p className="help-block">Configures predicted values should be calculated if not presented on import</p>
                                    <Checkbox<boolean, FvcSettingsScreenFormState> id="predictedValueCalculationsEnabled" field="predictedValueCalculationsEnabled" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Overreading</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Over-Read enabled</label>
                                    <Checkbox id="overreadEnabledToggle" className="checkbox" field="overreadEnabled" />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Final Test Acceptability Decision Maker</label>
                                    <p className="help-block">Configures whether the final test acceptability is make by the Over-Reader or the Technician</p>
                                    <Dropdown id="decisionMaker" field="decisionMaker" disabled={!formState.values.overreadEnabled}>
                                        <Option id="Overreader">Over-reader</Option>
                                        <Option id="Technician">Technician</Option>
                                    </Dropdown>
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-6">
                                    <label>Analysis Parameters</label>
                                    <p className="help-block">The test parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="selectedAnalysisParameters"
                                        field="selectedAnalysisParameters"
                                        options={analysisParameterOptions}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Repeatability Parameters</label>
                                    <p className="help-block">The repeatability parameters to be displayed in the over-reading details</p>
                                    <MultiSelect
                                        menuPlacement="auto"
                                        disabled={!formState.values.overreadEnabled}
                                        id="selectedRepeatabilityParameters"
                                        field="selectedRepeatabilityParameters"
                                        options={repeatabilityParameterOptions}
                                        labelKey="Text"
                                        valueKey="Value"
                                        multi
                                        allowSelectAll
                                    />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Allow Technician to Challenge Overreader Decision (Home Spirometry only)</label>
                                    <Checkbox id="technicianChallengeOverreaderDecisionToggle" className="checkbox" field="technicianChallengeOverreaderDecision" />
                                </div>
                                <div className="form-group col-md-6">
                                    <label>Show 5% of FVC</label>
                                    <Checkbox id="FivePercentFvc" className="checkbox" field="fivePercentFvc" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                        </div>
                    </div>
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const FvcSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<FvcSettingsScreenFormState>();
    const [parameters, setParameters] = React.useState([]);
    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const asyncEffect = async () => {
            const getParameters = async () => {
                const fvcParametersResult = await legacyClient.getParametersBySessionType(SessionTypes.Fvc);
                setParameters(fvcParametersResult);

                return fvcParametersResult;
            }

            const hydrate = async (fvcSettings: StudyFvcSetting, parameterResponse: ParameterEntity[], fivePercentEnabled: boolean) => {
                setInitialValues({
                    acceptableTests: fvcSettings.AcceptableTests,
                    maximumTests: fvcSettings.MaxTests,
                    overreadEnabled: fvcSettings.IsOverreadingAvailable,
                    testAcceptanceChange: fvcSettings.TestAcceptanceChange,
                    testAcceptanceChangeWindow: fvcSettings.TestAcceptanceChangeTimeWindow,
                    selectedTestParameters: ConvertToSelectListItemArray(fvcSettings.SelectedTestParameters, parameterResponse),
                    atsFEV1Repeatability: fvcSettings.AtsDeviationsFev1,
                    atsFEV1RepeatabilityValue: fvcSettings.AtsDeviationsFev1Volume,
                    atsFVCRepeatability: fvcSettings.AtsDeviationsFvc,
                    atsFVCRepeatabilityValue: fvcSettings.AtsDeviationsFvcVolume,
                    gradingEnabled: fvcSettings.GradingEnabled,
                    atsGuidelineRevision: fvcSettings.AtsGuidelineRevision,
                    decisionMaker: fvcSettings.SelectedFinalTestAcceptabilityDecisionMaker,
                    selectedAnalysisParameters: ConvertToSelectListItemArray(fvcSettings.SelectedAnalysisParameters, parameterResponse),
                    selectedRepeatabilityParameters: ConvertToSelectListItemArray(fvcSettings.SelectedRepeatabilityParameters, parameterResponse),
                    predictedValueCalculationsEnabled: fvcSettings.PredictedValueCalculationsEnabled,
                    technicianChallengeOverreaderDecision: fvcSettings.TechnicianChallengeOverreaderDecision,
                    fivePercentFvc: fivePercentEnabled,

                });
            }

            const parameterResponse = await getParameters();
            const fvcSettings = await legacyClient.getStudyFvcSettings(id);
            const fivePercentageEnabled = await legacyClient.getStudySetting(id, "Vitalograph.WebPortal.Data.Settings.Fvc.DisplayFivePercentOfFvcSetting");
            hydrate(fvcSettings, parameterResponse, fivePercentageEnabled.Value);
        }

        asyncEffect();
    }, [id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading FVC Settings..." />;
    }

    const validateFields = (values) => {
        return {
            selectedTestParameters: values.selectedTestParameters === null ? "At least one value must be selected" : undefined,
            selectedAnalysisParameters: values.selectedAnalysisParameters === null ? "At least one value must be selected" : undefined,
            selectedRepeatabilityParameters: values.selectedRepeatabilityParameters === null ? "At least one value must be selected" : undefined,
        }
    }

    return (
        <Form id="fvcSettingsForm" noValidate initialValues={initialValues} validateFields={validateFields}>
            <FvcSettingsScreenForm studyId={id} parameters={parameters} />
        </Form>
    );
}
